import React, { useState } from 'react';
import { Phone, Mail, Shield, Truck, Package, FileText, Heart, Thermometer, Award, Send, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    serviceType: '',
    deliveryFrequency: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/quote-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit quote request');
      }

      const data = await response.json();
      console.log('Quote request submitted successfully:', data);
      
      setFormSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setFormSubmitted(false);
        setFormData({
          name: '',
          organization: '',
          email: '',
          phone: '',
          serviceType: '',
          deliveryFrequency: '',
          message: ''
        });
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting quote request:', error);
      alert('Failed to submit quote request. Please try again or call us directly at 615-689-8841.');
    }
  };
  const services = [
    {
      icon: Package,
      title: "Lab Specimen Transport",
      description: "Secure, timely transport of medical specimens with proper handling and chain-of-custody documentation."
    },
    {
      icon: Truck,
      title: "Prescription Pickup and Delivery",
      description: "Reliable medication delivery services ensuring patients receive their prescriptions safely and on time."
    },
    {
      icon: Package,
      title: "Medical Supply Delivery",
      description: "Professional delivery of medical equipment and supplies to healthcare facilities and patients."
    },
    {
      icon: FileText,
      title: "Secure Document Transfer",
      description: "HIPAA-compliant transport of sensitive medical records and healthcare documents."
    },
    {
      icon: Heart,
      title: "Rural/Underserved Delivery Support",
      description: "Dedicated service to rural and underserved communities throughout Tennessee."
    },
    {
      icon: Thermometer,
      title: "Temperature-Controlled Transport",
      description: "Specialized handling for temperature-sensitive medical items with real-time monitoring."
    }
  ];

  const certifications = [
    {
      title: "HIPAA Privacy & Security Training",
      description: "Ensuring complete confidentiality and compliance with healthcare privacy regulations."
    },
    {
      title: "OSHA Healthcare Bloodborne Pathogens Training",
      description: "Certified in safe handling and transport of potentially infectious materials."
    },
    {
      title: "Healthcare & Billing Practices Compliance Training",
      description: "Expert knowledge in healthcare regulations and industry best practices."
    }
  ];

  const handleCall = () => {
    window.location.href = 'tel:6156898841';
  };

  const handleEmail = () => {
    window.location.href = 'mailto:Business@expproservices.com';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_eb48ffd4-45c2-41f3-b767-b48ba0d1b952/artifacts/tbr8744e_IMG_5102.jpeg" 
                alt="EXP Proficiency Logistics LLC" 
                className="h-20 w-auto"
              />
              <div className="hidden lg:block">
                <h1 className="text-xl font-bold text-slate-800">EXP Proficiency Logistics LLC</h1>
                <p className="text-sm text-gray-500">Medical & Secure Solutions</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-slate-700 hover:text-blue-900 transition-colors font-medium">Services</a>
              <a href="#certifications" className="text-slate-700 hover:text-blue-900 transition-colors font-medium">Certifications</a>
              <a href="#quote" className="text-slate-700 hover:text-blue-900 transition-colors font-medium">Get Quote</a>
              <a href="#contact" className="text-slate-700 hover:text-blue-900 transition-colors font-medium">Contact</a>
            </nav>
            <Button onClick={handleCall} className="bg-slate-800 hover:bg-blue-900 text-white transition-all duration-300">
              <Phone className="h-4 w-4 mr-2" />
              Call Us
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white py-20 md:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-12">
              <img 
                src="https://customer-assets.emergentagent.com/job_eb48ffd4-45c2-41f3-b767-b48ba0d1b952/artifacts/tbr8744e_IMG_5102.jpeg" 
                alt="EXP Proficiency Logistics" 
                className="w-auto mx-auto mb-8 drop-shadow-2xl"
                style={{ height: 'clamp(30rem, 60vw, 48rem)' }}
              />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Trusted Medical Courier Services Across Tennessee
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Reliable, safe, and timely transportation of lab specimens, prescriptions, medical supplies, and healthcare documents.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">About Our Mission</h2>
              <div className="w-24 h-1 bg-slate-800 mx-auto"></div>
            </div>
            <Card className="border-2 border-gray-100 shadow-lg">
              <CardContent className="p-8 md:p-12">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  <span className="font-semibold text-slate-900">EXP Proficiency Logistics LLC</span> is a specialized medical courier delivery service designed to provide reliable, safe, and timely transportation of lab specimens, prescriptions, medical supplies, and healthcare documents.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Our mission is to <span className="font-semibold text-slate-900">enhance healthcare efficiency</span> and support vulnerable and underserved communities throughout Tennessee by offering compliant, dependable logistics solutions.
                </p>
                <div className="bg-slate-50 border-l-4 border-slate-800 p-6 rounded-r-lg">
                  <h3 className="font-bold text-slate-900 mb-3 text-lg">We Serve:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-700">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-slate-800 rounded-full mr-3"></div>
                      <span>Clinics & Medical Centers</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-slate-800 rounded-full mr-3"></div>
                      <span>Independent Pharmacies</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-slate-800 rounded-full mr-3"></div>
                      <span>Hospitals & Laboratories</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-slate-800 rounded-full mr-3"></div>
                      <span>Home Healthcare Agencies</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-slate-800 rounded-full mr-3"></div>
                      <span>Assisted-Living Facilities</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-slate-800 rounded-full mr-3"></div>
                      <span>Rural Healthcare Providers</span>
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex flex-wrap gap-4 justify-center">
                  <Badge variant="outline" className="px-4 py-2 text-base border-slate-800 text-slate-800">Same-Day Delivery</Badge>
                  <Badge variant="outline" className="px-4 py-2 text-base border-slate-800 text-slate-800">Scheduled Service</Badge>
                  <Badge variant="outline" className="px-4 py-2 text-base border-slate-800 text-slate-800">On-Demand Transport</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive medical logistics solutions with a focus on accuracy, safety, and maintaining chain-of-custody
            </p>
            <div className="w-24 h-1 bg-slate-800 mx-auto mt-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:border-slate-800 transition-all duration-300 hover:shadow-xl group">
                  <CardHeader>
                    <div className="w-14 h-14 bg-slate-800 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-900 transition-colors duration-300">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <CardTitle className="text-xl text-slate-900">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 text-base leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Certifications & Compliance</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Fully trained and certified to handle your most sensitive medical logistics needs
            </p>
            <div className="w-24 h-1 bg-slate-800 mx-auto mt-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {certifications.map((cert, index) => (
              <Card key={index} className="border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="w-14 h-14 bg-blue-900 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Award className="h-7 w-7 text-white" />
                  </div>
                  <CardTitle className="text-lg text-slate-900 text-center leading-snug">{cert.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-center leading-relaxed">
                    {cert.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Request Quote Section */}
      <section id="quote" className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Request a Quote</h2>
              <p className="text-xl text-gray-600">
                Get a customized logistics solution for your healthcare facility
              </p>
              <div className="w-24 h-1 bg-slate-800 mx-auto mt-6"></div>
            </div>

            <Card className="border-2 border-gray-200 shadow-xl">
              <CardContent className="p-8 md:p-12">
                {formSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="h-10 w-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Quote Request Received!</h3>
                    <p className="text-lg text-gray-600 mb-2">
                      Thank you for your interest in our services.
                    </p>
                    <p className="text-gray-600">
                      We'll contact you within 24 hours to discuss your logistics needs.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-900 font-semibold">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="John Smith"
                          className="border-gray-300 focus:border-slate-800 focus:ring-slate-800"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="organization" className="text-slate-900 font-semibold">Organization *</Label>
                        <Input
                          id="organization"
                          name="organization"
                          type="text"
                          required
                          value={formData.organization}
                          onChange={handleInputChange}
                          placeholder="ABC Medical Center"
                          className="border-gray-300 focus:border-slate-800 focus:ring-slate-800"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-900 font-semibold">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@example.com"
                          className="border-gray-300 focus:border-slate-800 focus:ring-slate-800"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-900 font-semibold">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(615) 555-0123"
                          className="border-gray-300 focus:border-slate-800 focus:ring-slate-800"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="serviceType" className="text-slate-900 font-semibold">Service Type *</Label>
                        <Select
                          required
                          value={formData.serviceType}
                          onValueChange={(value) => handleSelectChange('serviceType', value)}
                        >
                          <SelectTrigger className="border-gray-300 focus:border-slate-800 focus:ring-slate-800">
                            <SelectValue placeholder="Select service type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lab-specimen">Lab Specimen Transport</SelectItem>
                            <SelectItem value="prescription">Prescription Pickup & Delivery</SelectItem>
                            <SelectItem value="medical-supply">Medical Supply Delivery</SelectItem>
                            <SelectItem value="document">Secure Document Transfer</SelectItem>
                            <SelectItem value="rural">Rural/Underserved Delivery</SelectItem>
                            <SelectItem value="temperature">Temperature-Controlled Transport</SelectItem>
                            <SelectItem value="multiple">Multiple Services</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="deliveryFrequency" className="text-slate-900 font-semibold">Delivery Frequency *</Label>
                        <Select
                          required
                          value={formData.deliveryFrequency}
                          onValueChange={(value) => handleSelectChange('deliveryFrequency', value)}
                        >
                          <SelectTrigger className="border-gray-300 focus:border-slate-800 focus:ring-slate-800">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="same-day">Same-Day (As Needed)</SelectItem>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="on-demand">On-Demand Only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-900 font-semibold">Additional Information</Label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your specific logistics needs, locations, special requirements, etc."
                        className="border-gray-300 focus:border-slate-800 focus:ring-slate-800"
                      />
                    </div>

                    <div className="pt-4">
                      <Button
                        type="submit"
                        size="lg"
                        className="w-full bg-slate-800 hover:bg-blue-900 text-white text-lg py-6 transition-all duration-300 shadow-lg hover:shadow-xl"
                      >
                        <Send className="h-5 w-5 mr-2" />
                        Submit Quote Request
                      </Button>
                    </div>

                    <p className="text-sm text-gray-500 text-center mt-4">
                      We respect your privacy and will never share your information. Response time: within 24 hours.
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-gray-300 mb-12">
              Contact us today for reliable medical courier services throughout Tennessee
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <Card className="bg-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer" onClick={handleCall}>
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Phone className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Call Us</h3>
                  <p className="text-2xl font-semibold text-blue-900">615-689-8841</p>
                  <p className="text-gray-600 mt-2">Available for immediate assistance</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer" onClick={handleEmail}>
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Mail className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Email Us</h3>
                  <p className="text-lg font-semibold text-blue-900 break-all">Business@expproservices.com</p>
                  <p className="text-gray-600 mt-2">We'll respond within 24 hours</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-4 mb-6 md:mb-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_eb48ffd4-45c2-41f3-b767-b48ba0d1b952/artifacts/tbr8744e_IMG_5102.jpeg" 
                alt="EXP Proficiency Logistics" 
                className="h-60 w-auto"
              />
              <div>
                <h3 className="font-bold text-lg">EXP Proficiency Logistics LLC</h3>
                <p className="text-gray-400 text-sm">Medical & Secure Solutions</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-gray-400 mb-2">Serving Healthcare Providers Across Tennessee</p>
              <p className="text-sm text-gray-500">© 2024 EXP Proficiency Logistics LLC. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
